import React, { createContext, ReactNode, useCallback, useMemo, useState, useEffect } from 'react';
import { AuthContextProps, hasAuthParams, useAuth } from 'react-oidc-context';
import { User } from 'oidc-client-ts';
import { setRenewTokenCallback } from '@/helpers/authFetch';
import authUtils from '@/helpers/authUtils';
import * as Const from '@/helpers/constants';
import { SESSION_EXPIRED } from '@/helpers/events';
import { formatFullRole, getBrowserLanguageAsDefault, getBrowserUtcAsDefault } from '@/helpers/utils';
import store from '@/redux/store';
import { setSettingsInfo, setUserInfo } from '@/redux/User/user.action';
import singletonEventService from '@/services/eventService';
import usersServices from '@/services/usersServices';

type Auth = {
  isLoading: boolean;
  isLogged: boolean;
  canAccess: () => boolean;
  canSeeFile: () => boolean;
  checkRole: (role: string) => boolean;
  checkRoles: (roles: string[]) => boolean;
  checkMultiRoleUser: () => boolean;
  getUserToken: () => string;
  getRoleSelected: (formatted: boolean) => string;
  getUserRole: (short: boolean) => string[];
  getUserRoles: () => string[];
  login: () => void;
  logout: () => Promise<void>;
  signout: () => Promise<void>;
  renewToken: () => Promise<User>;
  setRole: (role: string, fromBack: boolean) => void;
  setUserRoles: (roles: string[]) => void;
  setRoleSelected: (role: string) => void;
  auth: AuthContextProps | null;
  userCode: string | null;
  userPlant?: string;
  userRights: string[];
  eventService: any;
  mfaEnabled: boolean;
  clientRoot: string;
  authority: string;
  clientId: string;
  scope: string;
  nonce: string;
  state: string;
  creatorDepartment?: string;
  setStateRoleSelected: (role: string) => void;
  userRoles: string[];
  roleSelected: string;
  initAuth: () => void;
  isSubCRRole: boolean;
  isAdmin: boolean;
};

export const AuthContext = createContext<Auth | undefined>(undefined);

interface AuthProviderProps {
  children: ReactNode;
  defaultProps?: { userCode?: string; roleSelected?: string; userRoles?: string[] };
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children, defaultProps }) => {
  const [isLoading, setIsLoading] = useState(true);
  const [isLogged, setIsLogged] = useState(!!defaultProps?.roleSelected);
  const auth = useAuth();
  const [roleSelected, setRoleSelected] = useState<string>(defaultProps?.roleSelected || '');
  const [userPlant, setUserPlant] = useState<string | undefined>(undefined);
  const clientRoot = authUtils.ssoClientRoot;
  const authority = authUtils.ssoAuthority;
  const clientId = authUtils.ssoClientID;
  const scope = authUtils.ssoScopes;
  const { getCaRTT, setCaRTT } = authUtils;
  const nonce = authUtils.randomString(23);
  const state = authUtils.randomString(23);
  const [creatorDepartment, setCreatorDepartment] = useState<string | undefined>(undefined);
  const [userRights, setUserRights] = useState<string[]>([]);
  const [userRoles, setMappedUserRoles] = useState<string[]>(defaultProps?.userRoles || []);
  const [userCode, setUserCode] = useState<string | null>(defaultProps?.userCode || null);
  const isAuthReadyAndIdle = !hasAuthParams() && !auth.isAuthenticated && !auth.activeNavigator && !auth.isLoading;

  const clearOoidStore = () => Object.keys(window.localStorage)
    .filter((x) => x.includes('oidc'))
    .forEach((x) => localStorage.removeItem(x));

  const resetUser = (code: string) => {
    setUserCode(code);
    setUserRights([]);
    setMappedUserRoles([]);
    setRoleSelected('');
    clearOoidStore();
  };

  const getUser = (): User | null | undefined | void => {
    const { user } = auth;
    setCreatorDepartment(user?.profile?.department as string);
    setUserCode(user?.profile?.sub as string || '');
    setUserPlant(user?.profile?.plant_2 as string);
    setCaRTT(user?.access_token || getCaRTT());
    clearOoidStore();

    return user;
  };

  const getUserToken = (): string => {
    const { user } = auth;
    if (!user) return '';

    return user?.access_token || getCaRTT() || '';
  };

  const getUserRole = (short: boolean): string[] => {
    const roles = [roleSelected];

    return short ? roles.map((r) => r.replace(Const.CART_ROLE_PREFIX, '')) : roles;
  };

  const canAccess = (): boolean => userRoles?.length > 0;

  const checkRole = (role: string): boolean => {
    const fullRole = `${Const.CART_ROLE_PREFIX}${role}`;

    return roleSelected === fullRole;
  };

  const canSeeFile = (): boolean => checkRole(Const.CAMPAIGN_MANAGER_FILES);

  const checkRoles = (roles: string[] = []): boolean => roles.some((role) => checkRole(role));

  const signout = (): Promise<void> => {
    if (!auth) {
      return Promise.reject(new Error('auth is null'));
    }

    return auth.signoutPopup();
  };

  const setStateRoleSelected = useCallback((role: string) => {
    setRoleSelected(role);
  }, []);

  const setRole = (role: string, fromBack = false): void => {
    const selectedRole = fromBack ? (role ? authUtils.backRoleToOauthRole(role) : '') : role;
    setStateRoleSelected(selectedRole);
    setRoleSelected(selectedRole);
  };

  const setUserRoles = (roles: string[]): void => {
    setMappedUserRoles(authUtils.mapBackRolesToOauth(roles));
  };

  const getRoleSelected = (formatted: boolean): string => {
    if (formatted) return formatFullRole(roleSelected);

    return roleSelected;
  };

  const getUserRoles = (): string[] => userRoles || [];

  const checkMultiRoleUser = (): boolean => userRoles?.length > 1;

  const initLocalUser = async () => {
    let localAuth: any = {};

    try {
      localAuth = await import('../localAuth');
    } catch (error) {
      console.warn(`localAuth file not found.\nNode error: ${error}`);
    }

    try {
      const { LOCAL_AUTH_TOKEN, LOCAL_USER_NAME, LOCAL_USER_DEPARTMENT, LOCAL_USER_PLANT } = localAuth.default;
      resetUser(LOCAL_USER_NAME);
      setCaRTT(LOCAL_AUTH_TOKEN);
      const backUser = await usersServices.getUser();
      const userSettings = await usersServices.getUsersSettings(LOCAL_USER_NAME);
      setCreatorDepartment(LOCAL_USER_DEPARTMENT);
      setUserPlant(LOCAL_USER_PLANT);
      setRole(userSettings.roleSelected, true);
      setUserRoles(backUser.rolesList);
      sessionStorage.setItem(Const.STORAGE.USER_CODE, LOCAL_USER_NAME || '');
      setIsLogged(true);
      setIsLoading(false);
    } catch (error) {
      console.error(error); // eslint-disable-line no-console
    }
  };

  const refreshUserInfoAfterTokenRenew = useCallback(async (uid?: string | null) => {
    if (!uid) return;

    const [user, settings] = await Promise.all([
      usersServices.getCurrentUser(false),
      usersServices.getUsersSettings(uid),
    ]);

    store.dispatch(setUserInfo(user));
    store.dispatch(setSettingsInfo(settings));
  }, []);

  const renewToken = useCallback(async (): Promise<User> => {
    try {
      const renewed = await auth.signinSilent();
      setCaRTT(renewed?.access_token || getCaRTT());
      await refreshUserInfoAfterTokenRenew(renewed?.profile?.sub || userCode);

      return renewed as User;
    } catch (err) {
      throw new Error((err as { message?: string }).message);
    }
  }, [auth, getCaRTT, refreshUserInfoAfterTokenRenew, setCaRTT, userCode]);

  type UserSettingsType = { roleSelected?: string; language?: string; timezone?: string; [key: string]: unknown };
  const setRoleAndUpdateSettings = async (role: string, userrCode: string | null, userSettings: UserSettingsType) => {
    userSettings.roleSelected = authUtils.normalizeBackRoleForPersistence(role);
    userSettings.language = userSettings.language || getBrowserLanguageAsDefault();
    userSettings.timezone = userSettings.timezone || getBrowserUtcAsDefault();
    await usersServices.saveUsersSettings(userrCode, userSettings);
  };

  type BackUserRolesType = { roleSelected?: string; rolesList?: string[] };
  const hasBackRoles = (backUserData?: BackUserRolesType): boolean => Array.isArray(backUserData?.rolesList) && backUserData.rolesList.length > 0;

  const loadBackUserAndSettings = async (
    profileSub: string,
    updateBackRoles: boolean,
  ): Promise<{ backUserData: BackUserRolesType; userSettingsData: UserSettingsType; hasRoles: boolean }> => {
    const backUserData = await usersServices.getCurrentUser(updateBackRoles);
    const userSettingsData = await usersServices.getUsersSettings(profileSub);

    return {
      backUserData,
      userSettingsData,
      hasRoles: hasBackRoles(backUserData),
    };
  };

  const syncSelectedRoleWithSettings = async (
    userSettings: UserSettingsType,
    backUserData: BackUserRolesType,
    profileSub: string,
  ): Promise<string> => {
    const resolvedBackRole = authUtils.resolveSelectedBackRole(
      userSettings?.roleSelected,
      backUserData?.roleSelected,
      backUserData?.rolesList,
    );
    const currentSettingsBackRole = authUtils.normalizeBackRole(userSettings?.roleSelected);

    if (resolvedBackRole && currentSettingsBackRole !== resolvedBackRole) {
      await setRoleAndUpdateSettings(resolvedBackRole, profileSub, userSettings);
    }

    return resolvedBackRole;
  };

  const fetchUser = async () => {
    try {
      const user = getUser();

      if ((!user || user.expired)) {
        sessionStorage.setItem(Const.STORAGE.SIGN_IN_REDIRECT, window.location.pathname + window.location.search);
        setUserCode(null);
        setIsLogged(false);

        return;
      }
      sessionStorage.setItem(Const.STORAGE.USER_CODE, userCode || '');

      const updateBackRoles = Boolean(sessionStorage.getItem(Const.STORAGE.LAST_ACTION_WAS_LOGIN) && !Const.isAzureEnv);
      const resetTokenTried = Boolean(sessionStorage.getItem(Const.STORAGE.RESET_TOKEN_TRIED));
      const {
        backUserData: backUser,
        userSettingsData: userSettings,
        hasRoles,
      } = await loadBackUserAndSettings(user.profile.sub, updateBackRoles);

      if (hasRoles || Const.isAzureEnv) { // if had some role // @azureFix remove when back solve RD conectivity
        const resolvedBackRole = await syncSelectedRoleWithSettings(userSettings, backUser, user.profile.sub);

        setRole(resolvedBackRole, true);
        setUserRoles(backUser.rolesList || []);
        sessionStorage.removeItem(Const.STORAGE.RESET_TOKEN_TRIED);
      } else if (!updateBackRoles && !resetTokenTried) { // if no role and not first login and not tried to reset token
        sessionStorage.setItem(Const.STORAGE.RESET_TOKEN_TRIED, '1');

        try {
          await renewToken();
        } catch (error) {
          console.error('Silent token renewal failed:', error); // eslint-disable-line no-console
        }

        const {
          backUserData: backUserRe,
          userSettingsData: userSettingsRe,
          hasRoles: hasRolesRe,
        } = await loadBackUserAndSettings(user.profile.sub, true);

        if (hasRolesRe) {
          const resolvedBackRole = await syncSelectedRoleWithSettings(userSettingsRe, backUserRe, user.profile.sub);

          setRole(resolvedBackRole, true);
          setUserRoles(backUserRe.rolesList || []);
        }
        sessionStorage.removeItem(Const.STORAGE.RESET_TOKEN_TRIED);
      }
      setIsLogged(true);
    } catch (error: unknown) {
      console.log('You are not logged in. Error:', error); // eslint-disable-line no-console
      // get error number 403 and login without permisions
      const status = (error as { status?: number })?.status;

      if (status === 403) {
        setUserCode(null);
        setIsLogged(true);
      }
    } finally {
      sessionStorage.removeItem(Const.STORAGE.LAST_ACTION_WAS_LOGIN);
      clearOoidStore();
      setIsLoading(false);
    }
  };

  const eventService = useMemo(() => singletonEventService, []);

  const mfaEnabled = false;

  const removeUser = (): Promise<User | null | undefined> => {
    if (!auth) {
      return Promise.reject(new Error('auth is null'));
    }

    return auth.removeUser().then((removed) => {
      resetUser('');

      return removed as User | null | undefined;
    });
  };

  const login = (): Promise<void> => {
    sessionStorage.setItem(Const.STORAGE.LAST_ACTION_WAS_LOGIN, '1');

    if (!auth) {
      return Promise.reject(new Error('auth is null'));
    }

    return auth.signinRedirect();
  };

  const initAuth = async () => {
    /* if (Const.env === 'local') {
      await initLocalUser();
    } */
  };

  const logout = async (): Promise<void> => {
    await removeUser();

    if (!auth) throw new Error('auth is null');
    await auth.signoutRedirect();
    setIsLogged(false);
  };

  const isSubCRRole = checkRoles(
    [Const.CART_PLANT_SUPERVISOR, Const.CART_QMB_PLANT_APPROVER, Const.QMB_PLANT_APPROVER_JV, Const.VAN_E3_QMB_PLANT_APPROVER, Const.CAMPAIGN_MANAGER,
      Const.VAN_CAMPAIGN_MANAGER, Const.CAMPAIGN_MANAGER_JV],
  );

  const isAdmin = checkRole(Const.ADMIN);

  useEffect(() => {
    if (!auth.isAuthenticated) return undefined;

    const handleTokenExpiring = () => {
      renewToken().catch((err) => console.log('[AuthContext] Error in token renew:', err)); // eslint-disable-line no-console
    };

    const handleTokenExpired = async () => {
      if (!isLogged) {
        return;
      }

      try {
        await renewToken();
      } catch (err) {
        resetUser('');
        setIsLogged(false);
        eventService.emit(SESSION_EXPIRED);
      }
    };

    auth.events.addAccessTokenExpiring(handleTokenExpiring);
    auth.events.addAccessTokenExpired(handleTokenExpired);

    return () => {
      auth.events.removeAccessTokenExpiring(handleTokenExpiring);
      auth.events.removeAccessTokenExpired(handleTokenExpired);
    };
  }, [auth.isAuthenticated, renewToken]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    const renewTokenForAuthFetch = async () => {
      try {
        await renewToken();
      } catch (error) {
        throw error;
      }
    };

    setRenewTokenCallback(renewTokenForAuthFetch);
  }, [renewToken]);

  useEffect(() => {
    if (!userCode) return;
    fetchUser();
  }, [userCode]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (auth.error) {
      window.history.replaceState({}, document.title, window.location.pathname);
      window.location.reload();

      return;
    }

    try {
      const actualRoute = window.location.pathname + window.location.search;

      if (isAuthReadyAndIdle) {
        if (actualRoute !== '/') sessionStorage.setItem(Const.STORAGE.SIGN_IN_REDIRECT, actualRoute);
        sessionStorage.setItem(Const.STORAGE.LAST_ACTION_WAS_LOGIN, '1');

        const isLoginPath = window.location.pathname === `${Const.getAbsoluteRoute(Const.ROUTES.LOGIN)}`;

        if (!isLoginPath) {
          auth.signinRedirect().catch((error) => {
            console.error('[AuthContext] Auto signin redirect failed:', error); // eslint-disable-line no-console
            setIsLoading(false);
          });

          return;
        }

        setIsLoading(false);

        return;
      }
      if (!auth.user) return;

      if (window.location.pathname !== `${Const.getAbsoluteRoute(Const.ROUTES.LOGIN)}` && window.location.pathname !== `${Const.BASE_PATH}` && window.location.pathname !== '/') {
        sessionStorage.setItem(Const.STORAGE.SIGN_IN_REDIRECT, actualRoute);
      }
      setUserCode(auth.user?.profile.sub);
      setCaRTT(auth.user?.access_token || 'no_token');
    } catch (error) {
      console.error(error); // eslint-disable-line no-console
    }
  }, [auth]);

  const value: Auth = {
    isLoading,
    isLogged,
    canAccess,
    canSeeFile,
    checkRole,
    checkRoles,
    checkMultiRoleUser,
    getRoleSelected,
    getUserToken,
    getUserRole,
    getUserRoles,
    login,
    logout,
    signout,
    renewToken,
    setRole,
    setUserRoles,
    setRoleSelected,
    auth,
    userCode,
    userPlant,
    userRights,
    eventService,
    mfaEnabled,
    clientRoot,
    authority,
    clientId,
    scope,
    nonce,
    state,
    creatorDepartment,
    setStateRoleSelected,
    userRoles,
    roleSelected,
    initAuth,
    isSubCRRole,
    isAdmin,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
